/**
 * MODULE: domain/pages/frontend/utils/crmBrainQueryUtils.ts
 * PURPOSE: React Query keys and invalidation for CRM/page brain context after engagement signals.
 * DEPENDENCIES: @tanstack/react-query, assistantContextApi, pageBrainFocusUtils
 * PUBLIC_API: CRM_BRAIN_QUERY_KEYS, fetchBrainContextForFocus, invalidatePageEngagementBrainQueries, ENGAGEMENT_REFETCH_DELAYS_MS
 * SIDE_EFFECTS: Schedules timed query invalidations after page_engaged-style actions
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 128 / 150 lines
 * RECENT: 2026-05-26 [2.2.1251] — notifyBrainActivityChanged on page engagement refresh.
 * RECENT: 2026-05-25 [2.2.1202] — export engagement refetch delays for CRM entity sync.
 * RECENT_PRIOR: 2026-05-25 [2.2.1200] — engagement invalidation + delayed refetch for fan-out lag.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  resolveAssistantContext,
  type AssistantContextResponse,
} from '@shared/lib/frontend/api/assistantContextApi';
import { notifyBrainActivityChanged } from '@shared/lib/frontend/utils/brainActivityEvents';
import type { Page } from '../../contracts/pages.types';
import { pageBrainResolveUserMessage, type PageBrainFocusKind } from './pageBrainFocusUtils';

export type PageEngagementBrainLinks = Pick<
  Page,
  | 'id'
  | 'linked_deal_id'
  | 'linked_deal_label'
  | 'linked_contact_id'
  | 'linked_contact_label'
  | 'linked_company_id'
  | 'linked_company_label'
>;

export type BrainContextFetchInput = {
  kind: PageBrainFocusKind;
  entityId: string;
  entityLabel?: string | null;
  signal?: AbortSignal;
};

/** Delays account for orchestration fan-out before insights catch up to activity_events. */
export const ENGAGEMENT_REFETCH_DELAYS_MS = [0, 5_000, 30_000] as const;

export const CRM_BRAIN_QUERY_KEYS = {
  prefix: () => ['pages', 'brain-context'] as const,
  context: (kind: PageBrainFocusKind, entityId: string, entityLabel = '') =>
    ['pages', 'brain-context', kind, entityId, entityLabel.trim()] as const,
};

export async function fetchBrainContextForFocus(
  input: BrainContextFetchInput,
): Promise<AssistantContextResponse> {
  const { kind, entityId, entityLabel = '', signal } = input;
  const label = entityLabel?.trim() || '';
  const userMessage = pageBrainResolveUserMessage(kind, label, label || entityId, entityId);

  return resolveAssistantContext(
    {
      query_type: 'general_chat',
      user_message: userMessage,
      focus_entity_hint: {
        kind,
        query: label || entityId,
        entity_id: entityId,
      },
    },
    signal,
  );
}

function invalidateEntityBrainContext(
  qc: QueryClient,
  kind: PageBrainFocusKind,
  entityId: string | null | undefined,
): void {
  const id = entityId?.trim();
  if (!id || kind === 'page') return;
  void qc.invalidateQueries({
    queryKey: ['pages', 'brain-context', kind, id],
  });
}

/** Invalidate brain activity + CRM context resolve caches tied to a page's CRM links. */
export function invalidatePageEngagementBrainQueries(
  qc: QueryClient,
  page: PageEngagementBrainLinks,
): void {
  void qc.invalidateQueries({ queryKey: ['pages', page.id, 'brain-activity'] });
  invalidateEntityBrainContext(qc, 'deal', page.linked_deal_id);
  invalidateEntityBrainContext(qc, 'contact', page.linked_contact_id);
  invalidateEntityBrainContext(qc, 'account', page.linked_company_id);
  void qc.invalidateQueries({ queryKey: ['pages', 'by-deal'] });
  void qc.invalidateQueries({ queryKey: ['pages', 'by-company'] });
  void qc.invalidateQueries({ queryKey: ['pages', 'by-contact'] });
}

/** Immediate + delayed invalidation so drawer badges pick up new engagement insights. */
export function refreshPageEngagementBrainQueries(
  qc: QueryClient,
  page: PageEngagementBrainLinks,
): void {
  notifyBrainActivityChanged({
    source: 'page_engaged',
    entity_type: page.linked_deal_id ? 'deal' : page.linked_contact_id ? 'contact' : 'account',
    entity_id: page.linked_deal_id || page.linked_contact_id || page.linked_company_id || undefined,
    scope: 'all',
  });
  for (const delayMs of ENGAGEMENT_REFETCH_DELAYS_MS) {
    if (delayMs === 0) {
      invalidatePageEngagementBrainQueries(qc, page);
      continue;
    }
    window.setTimeout(() => {
      invalidatePageEngagementBrainQueries(qc, page);
    }, delayMs);
  }
}

export function refreshPageEngagementBrainQueriesByPageId(
  qc: QueryClient,
  pageId: string,
  page?: PageEngagementBrainLinks | null,
): void {
  if (page?.id === pageId) {
    refreshPageEngagementBrainQueries(qc, page);
    return;
  }
  void qc.invalidateQueries({ queryKey: ['pages', pageId, 'brain-activity'] });
  void qc.invalidateQueries({ queryKey: CRM_BRAIN_QUERY_KEYS.prefix() });
}
